import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { UserService } from '../auth/user.service';
import { AlertController } from '@ionic/angular';
import { StiControllerApi } from '../../../api/services/sti-controller-api';
import { firstValueFrom } from 'rxjs';
import { StiDto } from '../../../api/models/sti-dto';
import { StiEtapeDto } from '../../../api/models/sti-etape-dto';

/**
 * Durée par défaut et max pour créer un sti par étapes
 */
export const FIXED_DURATION = {
  aller: {
    default: '00:30',
    max: '01:30',
  },
  intervention: {
    default: '01:00',
    max: '02:00',
  },
  retour: {
    default: '00:30',
    max: '01:30',
  },
  global: {
    default: '02:00',
    max: '02:00',
  },
};

/**
 * Enumération pour les actions d'étapes de STI possible
 */
export enum ActionEtapeSTI {
  /**
   * Une seule étape globale dans le cas d'un STI global
   */
  SUSPENSION = 'SUSPENSION',
  /**
   * Etape de déplacement aller
   */
  REDEMARRAGE = 'REDEMARRAGE',
  /**
   * Etape d'intervention
   */
  CLOTURE_ETAPE = 'CLOTURE_ETAPE_COURANTE',
}

/**
 * Enumération pour les types d'étapes de STI possible
 */
export enum TypeEtapeSTI {
  /**
   * Une seule étape globale dans le cas d'un STI global
   */
  GLOBAL = 'global',
  /**
   * Etape de déplacement aller
   */
  DEPLACEMENT_ALLER = 'deplacement_aller',
  /**
   * Etape de déplacement retour
   */
  DEPLACEMENT_RETOUR = 'deplacement_retour',
  /**
   * Etape d'intervention
   */
  INTERVENTION = 'intervention',
}

/**
 * Enumération contenant les statuts d'une étape du STI possible
 */
export enum StatusTypeSTI {
  /**
   * L'étape n'a pas encore été démarrée
   */
  NON_DEMARRE = 'non_demarre',
  /**
   * L'étape est en cours
   */
  EN_COURS = 'en_cours',
  /**
   * L'étape est terminée
   */
  TERMINE = 'termine',
  /**
   * L'étape est suspendue
   */
  SUSPENDU = 'suspendu',
}

/**
 * Provider pour la gestion des chronomètres sur les STI
 *
 * @export
 * @class StiProvider
 */
@Injectable({ providedIn: 'root' })
export class StiProvider {
  private stiApi = inject(StiControllerApi);

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   */
  constructor(
    private translate: TranslateService,
    private alertController: AlertController,
    private loggerFactory: LoggerFactory,
    private userService: UserService
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  /**
   * Action sur une étape d'un sti
   */
  async actionSTI(idSti: string, action: ActionEtapeSTI) {
    let result = await this.getSTI(idSti);
    if (result == null) return null;

    try {
      await firstValueFrom(
        this.stiApi.changementStatutSti({
          idSti: idSti,
          body: { action },
        })
      );
    } catch (error) {
      this.logger.error(error, `StiChronoProvider : actionSTI for idSti: ${idSti} and action: ${action}`);
      return null;
    }
    return true;
  }

  /**
   * Récupération d'un STI à partir de son id
   */
  async getSTI(idSti: string): Promise<StiDto> {
    const sti = await firstValueFrom(
      this.stiApi.getStiById({
        idSti: idSti,
      })
    );
    if (sti) {
      sti.etapes = this.sortSteps(sti.etapes);
      return sti;
    }
    return null;
  }

  /**
   * Récupère les STIS associés au techicien courant pour cet OT
   * @param id_ot id de l'ordre de travail auquel est associé le STI
   * @returns la liste des STIs associés au technicien pour cet OT
   */
  getSTIs(id_ot: string): Promise<StiDto[]> {
    return firstValueFrom(
      this.stiApi.getStis({
        idOt: id_ot,
        idIntervenant: this.userService.getId(),
      })
    );
  }

  /**
   * Affiche une alerte demandant si la personne veut lancer un trajet retour
   * @returns {Promise} promesse qui se résoud si la personne accepte, rejette si la personne refuse
   */
  async alertReturnTravel(): Promise<any> {
    let alert = await this.alertController.create({
      header: this.translate.instant('CHRONO.STI.START_RETURN_TRAVEL'),
      buttons: [
        {
          text: this.translate.instant('GLOBAL.NO'),
          role: 'no',
        },
        {
          text: this.translate.instant('GLOBAL.YES'),
          role: 'yes',
        },
      ],
      mode: 'ios',
      backdropDismiss: false,
    });
    await alert.present();
  }

  /**
   * Calcule le statut global du STI
   * à partir du statut de ses étapes
   * @param etapes liste des étapes du STI
   * @returns le statut du STI
   */
  public getSTIStatus(etapes: StiEtapeDto[]): StatusTypeSTI {
    let status = StatusTypeSTI.NON_DEMARRE;
    // non démarré => toutes les étapes sont à non démarré
    const isNotStarted: boolean = etapes.every((currentStep) => {
      return currentStep.statut === StatusTypeSTI.NON_DEMARRE;
    });
    if (!isNotStarted) {
      // en cours => une des étapes est en cours
      const isInProgress: boolean = etapes.some((currentStep) => {
        return currentStep.statut === StatusTypeSTI.EN_COURS;
      });
      if (isInProgress) {
        status = StatusTypeSTI.EN_COURS;
      } else {
        // suspendu => une des étapes est suspendue et aucune n'est en cours
        const isSuspended: boolean = etapes.some((currentStep) => {
          return currentStep.statut === StatusTypeSTI.SUSPENDU;
        });
        if (isSuspended) {
          status = StatusTypeSTI.SUSPENDU;
        } else {
          // terminé => toutes les étapes sont à terminer
          const isFinished: boolean = etapes.every((currentStep) => {
            return currentStep.statut === StatusTypeSTI.TERMINE;
          });
          if (isFinished) {
            status = StatusTypeSTI.TERMINE;
          } else {
            status = StatusTypeSTI.EN_COURS;
          }
        }
      }
    }
    return status;
  }

  /**
   * Tri les étapes par ordre de déroulement (aller, intervention, retour)
   * @param etapes les étapes à trier
   * @returns {StiEtapeDto[]} les étapes triées par ordre de déroulement
   */
  private sortSteps(etapes: StiEtapeDto[]): StiEtapeDto[] {
    return etapes.sort((stepA: StiEtapeDto, stepB: StiEtapeDto) => {
      if (stepA.type_etape === TypeEtapeSTI.DEPLACEMENT_ALLER) {
        return -1;
      } else if (stepA.type_etape === TypeEtapeSTI.INTERVENTION) {
        if (stepB.type_etape === TypeEtapeSTI.DEPLACEMENT_ALLER) {
          return 1;
        } else if (stepB.type_etape === TypeEtapeSTI.DEPLACEMENT_RETOUR) {
          return -1;
        }
      } else if (stepA.type_etape === TypeEtapeSTI.DEPLACEMENT_RETOUR) {
        return 1;
      }
      return 0;
    });
  }
}
